import React from 'react';
import {
  Eye, SquarePen, Trash2, PlayCircle, Flag, Ban,
  BadgeDollarSign, UserRoundCheck, UserRoundX, LucideIcon,
} from 'lucide-react';

/**
 * Botoes de acao das listagens.
 *
 * Antes cada tela repetia a mesma classe enorme do Tailwind em cada botao, todos
 * cinzas e so com icone: para saber o que cada um fazia era preciso passar o mouse
 * por cima. Aqui cada acao tem cor propria e nome escrito, e a aparencia fica
 * definida num lugar so, de modo que uma mudanca de estilo alcanca o sistema todo.
 *
 * O nome some abaixo de 640px de largura, onde nao ha espaco para varios botoes
 * com texto numa linha da tabela. O title continua atendendo esse caso e o leitor
 * de tela.
 */

export type TomDeAcao =
  | 'azul'      // ver, consultar
  | 'indigo'    // editar
  | 'vermelho'  // excluir, rejeitar, cancelar
  | 'verde'     // iniciar, aprovar, ativar
  | 'esmeralda' // confirmar pagamento
  | 'ambar'     // desativar, suspender
  | 'cinza';    // acoes neutras

const TONS: Record<TomDeAcao, string> = {
  azul: 'text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100 hover:border-blue-300 focus:ring-blue-500',
  indigo: 'text-indigo-700 bg-indigo-50 border-indigo-200 hover:bg-indigo-100 hover:border-indigo-300 focus:ring-indigo-500',
  vermelho: 'text-red-700 bg-red-50 border-red-200 hover:bg-red-100 hover:border-red-300 focus:ring-red-500',
  verde: 'text-green-700 bg-green-50 border-green-200 hover:bg-green-100 hover:border-green-300 focus:ring-green-500',
  esmeralda: 'text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 focus:ring-emerald-500',
  ambar: 'text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100 hover:border-amber-300 focus:ring-amber-500',
  cinza: 'text-gray-700 bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300 focus:ring-gray-400',
};

interface BotaoAcaoProps {
  tom: TomDeAcao;
  icone: LucideIcon;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  title?: string;
  type?: 'button' | 'submit';
}

export const BotaoAcao: React.FC<BotaoAcaoProps> = ({
  tom, icone: Icone, children, onClick, disabled, title, type = 'button',
}) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    title={title ?? (typeof children === 'string' ? children : undefined)}
    className={`inline-flex items-center gap-1.5 px-2 py-1.5 border rounded-lg text-xs font-semibold
      shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1
      disabled:opacity-50 disabled:cursor-not-allowed ${TONS[tom]}`}
  >
    <Icone className="h-4 w-4 shrink-0" />
    <span className="hidden sm:inline">{children}</span>
  </button>
);

/**
 * Container da coluna de acoes.
 *
 * O flex-wrap evita que uma linha com cinco acoes (viagens, por exemplo) estoure a
 * largura da tabela. A largura minima existe porque, so com o wrap, o navegador
 * espremia a coluna ate a largura de um unico botao e empilhava tudo em coluna:
 * com ela cabem tres por linha e as quebras ficam alinhadas entre as linhas.
 */
export const AcoesDaLinha: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex flex-wrap justify-end items-center gap-2 sm:min-w-[11rem]">{children}</div>
);

// Atalhos das acoes que se repetem em quase toda listagem, para que cor, icone e
// nome nao divirjam de uma tela para outra.

type AtalhoProps = { onClick?: () => void; disabled?: boolean; title?: string };

export const BotaoVer: React.FC<AtalhoProps> = (p) => (
  <BotaoAcao tom="azul" icone={Eye} {...p}>Ver</BotaoAcao>
);

export const BotaoEditar: React.FC<AtalhoProps> = (p) => (
  <BotaoAcao tom="indigo" icone={SquarePen} {...p}>Editar</BotaoAcao>
);

export const BotaoExcluir: React.FC<AtalhoProps> = (p) => (
  <BotaoAcao tom="vermelho" icone={Trash2} {...p}>Excluir</BotaoAcao>
);

export const BotaoIniciar: React.FC<AtalhoProps> = (p) => (
  <BotaoAcao tom="verde" icone={PlayCircle} {...p}>Iniciar</BotaoAcao>
);

export const BotaoFinalizar: React.FC<AtalhoProps> = (p) => (
  <BotaoAcao tom="azul" icone={Flag} {...p}>Finalizar</BotaoAcao>
);

export const BotaoCancelar: React.FC<AtalhoProps> = (p) => (
  <BotaoAcao tom="vermelho" icone={Ban} {...p}>Cancelar</BotaoAcao>
);

export const BotaoPagar: React.FC<AtalhoProps> = (p) => (
  <BotaoAcao tom="esmeralda" icone={BadgeDollarSign} {...p}>Pagar</BotaoAcao>
);

export const BotaoAtivar: React.FC<AtalhoProps> = (p) => (
  <BotaoAcao tom="verde" icone={UserRoundCheck} {...p}>Ativar</BotaoAcao>
);

export const BotaoDesativar: React.FC<AtalhoProps> = (p) => (
  <BotaoAcao tom="ambar" icone={UserRoundX} {...p}>Desativar</BotaoAcao>
);
